import { useEffect, useRef, useState } from "react";
import { loadGoogleMaps } from "../utils/loadGoogleMaps";

/**
 * AddressAutocomplete
 *
 * Uses Google Places API (New) only.
 *
 * Returns:
 * {
 *   placeId: string,
 *   address: string,
 *   lat: number,
 *   lng: number
 * }
 *
 * No legacy Places API.
 * No fake coordinates.
 * No random fallback coordinates.
 */
export default function AddressAutocomplete({
  placeholder = "Search address...",
  onSelect,
  disabled = false,
}) {
  const containerRef = useRef(null);
  const autocompleteRef = useRef(null);
  const listenerRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    const initializeAutocomplete = async () => {
      try {
        setLoading(true);
        setError("");

        const google = await loadGoogleMaps();

        if (cancelled || !containerRef.current) {
          return;
        }

        if (!google?.maps) {
          throw new Error("Google Maps failed to load.");
        }

        if (!google.maps.places) {
          throw new Error(
            "Google Places library is not available."
          );
        }

        /**
         * Only use Places API (New).
         *
         * We intentionally do NOT fall back to:
         * google.maps.places.Autocomplete
         *
         * because that is the legacy API.
         */
        if (!google.maps.places.PlaceAutocompleteElement) {
          throw new Error(
            "Google Places API (New) PlaceAutocompleteElement is not available."
          );
        }

        /**
         * Create the new Places autocomplete element.
         */
        const autocomplete =
          new google.maps.places.PlaceAutocompleteElement({
            // includedRegionCodes is the PlaceAutocompleteElement option;
            // componentRestrictions is the legacy Autocomplete one, kept for
            // older loader versions that still read it.
            includedRegionCodes: ["ng"],
            componentRestrictions: {
              country: ["ng"],
            },
          });

        autocomplete.setAttribute(
          "placeholder",
          placeholder
        );

        /**
         * Keep a reference for cleanup.
         */
        autocompleteRef.current = autocomplete;

        /**
         * Clear any existing content.
         */
        containerRef.current.innerHTML = "";

        /**
         * Add autocomplete element to DOM.
         */
        containerRef.current.appendChild(autocomplete);

        /**
         * Handle place selection.
         *
         * The selected prediction contains a Place ID.
         *
         * We then create a Place object and explicitly
         * request the fields required by the application.
         */
        const handlePlaceSelect = async (event) => {
          try {
            setError("");

            /**
             * Depending on the Maps JS API version,
             * the selected prediction may be exposed
             * in event.placePrediction.
             */
            const prediction =
              event.placePrediction ||
              event.detail?.placePrediction;

            if (!prediction) {
              console.error(
                "No place prediction found:",
                event
              );

              setError(
                "Could not identify the selected location. Please try again."
              );

              return;
            }

            /**
             * Extract Place ID.
             */
            const placeId =
              prediction.placeId ||
              prediction.place_id;

            if (!placeId) {
              throw new Error(
                "Selected place does not have a valid Place ID."
              );
            }

            /**
             * Create a Place object using the new API.
             */
            const place =
              new google.maps.places.Place({
                id: placeId,
              });

            /**
             * Explicitly request the fields we need.
             *
             * location is required for latitude/longitude.
             */
            await place.fetchFields({
              fields: [
                "id",
                "formattedAddress",
                "location",
              ],
            });

            /**
             * Validate location.
             */
            if (!place.location) {
              throw new Error(
                "Google Maps did not return coordinates for this location."
              );
            }

            /**
             * The modern Place API exposes:
             *
             * place.location.lat()
             * place.location.lng()
             *
             * Normalize them to numbers.
             */
            const lat =
              typeof place.location.lat === "function"
                ? place.location.lat()
                : place.location.lat;

            const lng =
              typeof place.location.lng === "function"
                ? place.location.lng()
                : place.location.lng;

            /**
             * Validate coordinates.
             */
            if (
              typeof lat !== "number" ||
              typeof lng !== "number" ||
              !Number.isFinite(lat) ||
              !Number.isFinite(lng)
            ) {
              throw new Error(
                "Invalid coordinates returned by Google Maps."
              );
            }

            /**
             * Normalize the result.
             */
            const selectedPoint = {
              placeId:
                place.id ||
                placeId,

              address:
                place.formattedAddress ||
                "",

              lat,
              lng,
            };

            console.log(
              "Selected delivery location:",
              selectedPoint
            );

            /**
             * Return the normalized location
             * to PlaceOrder.
             */
            onSelect(selectedPoint);
          } catch (err) {
            console.error(
              "Failed to resolve selected place:",
              err
            );

            setError(
              err.message ||
                "Could not retrieve the selected location. Please try again."
            );
          }
        };

        /**
         * Register selection event.
         */
        autocomplete.addEventListener(
          "gmp-select",
          handlePlaceSelect
        );

        listenerRef.current = {
          element: autocomplete,
          handler: handlePlaceSelect,
        };

        setLoading(false);
      } catch (err) {
        console.error(
          "Google Places initialization failed:",
          err
        );

        if (!cancelled) {
          setLoading(false);
          setError(
            err.message ||
              "Unable to load address search."
          );
        }
      }
    };

    initializeAutocomplete();

    /**
     * Cleanup.
     */
    return () => {
      cancelled = true;

      if (
        listenerRef.current?.element &&
        listenerRef.current?.handler
      ) {
        listenerRef.current.element.removeEventListener(
          "gmp-select",
          listenerRef.current.handler
        );
      }

      if (containerRef.current) {
        containerRef.current.innerHTML = "";
      }

      autocompleteRef.current = null;
      listenerRef.current = null;
    };
  }, [placeholder, onSelect]);

  return (
    <div className="autocomplete-wrapper">
      <div
        ref={containerRef}
        className="autocomplete-container"
      />

      {loading && (
        <p className="hint">
          Loading address search...
        </p>
      )}

      {error && (
        <p className="error">
          {error}
        </p>
      )}
    </div>
  );
}